import { ExpandLess, ExpandMore } from "@mui/icons-material"
import {
    AlertTitle,
    Box,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Stack,
    Typography,
} from "@mui/material"
import React, { Context, ReactElement, useState } from "react"
import { ErrorCode, FileRejection } from "react-dropzone"
import { useForm } from "react-hook-form"
import { TransitionGroup } from "react-transition-group"
import { KeyedMutator } from "swr"

import AttachmentMetaForm from "./attachmentMetaForm"
import {
    DEVICE_MAX_ALLOWED_ATTACHMENTS,
    DEVICE_MAX_FILES_PER_DROP,
    DEVICE_MAX_FILE_SIZE_BYTES,
    DEVICE_MIN_FILE_SIZE_BYTES,
} from "../../../../../constants/attachmentUploadRestrictions"
import { Datasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { useScreenSize } from "../../../../../hooks/useScreenSize"
import { UploadAttachmentResult } from "../../../../../services/middleware/hooks/devices/common/useUploadDeviceAttachments"
import useDeleteUploadedAttachment from "../../../../../services/middleware/hooks/devices/ttr/useDeleteUploadedAttachment"
import useSetUploadedAttachmentRestricted from "../../../../../services/middleware/hooks/devices/ttr/useSetUploadedAttachmentRestricted"
import { TAttachmentSummary } from "../../../../../services/middleware/types/devices/ttr/listAttachmentSummariesTypes"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import { bytesToMb, snackbar } from "../../../../../utils/general/generalUtils"
import {
    getAttachmentDescriptionRegisterName,
    getAttachmentRestrictedRegisterName,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import AppDropzone from "../../../../attachments/appDropzone"
import AppAlert from "../../../../dataDisplay/alerts/appAlert"
import CheckMarkList from "../../../../dataDisplay/lsits/checkMarkList"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import DeviceAttachmentAccordionSkeleton from "../../../../loading/skeletons/deviceAttachmentsAccordionSkeleton"
import { AppCard, AppCardHeader } from "../../../../surfaces/appCard"
import DisplayIfElse from "../../../../wrappers/displayIfElse"
import {
    DeviceAttachment,
    IDeviceFormContextValue,
} from "../../../types/providerTypes"
import { DeviceFormDataWithAttachments } from "../../types/providerTypes"

/** Returns a mapping of register names to values. Used to populate the react-hook-form fields on mount */
const mapRegisterNameToValues = (
    formData: DeviceFormDataWithAttachments,
    uploadedAttachments?: TAttachmentSummary[]
): { [name: string]: NonNullable<unknown> } => {
    const mappings: { [name: string]: NonNullable<unknown> } = {}

    formData.stagedAttachments.forEach((stagedAttachment) => {
        mappings[
            getAttachmentDescriptionRegisterName(stagedAttachment.file.name)
        ] = stagedAttachment.description

        mappings[
            getAttachmentRestrictedRegisterName(stagedAttachment.file.name)
        ] = stagedAttachment.restricted
    })

    if (uploadedAttachments) {
        uploadedAttachments.forEach((summary) => {
            mappings[getAttachmentRestrictedRegisterName(summary.fileName)] =
                summary.private
        })
    }

    return mappings
}

export interface IAttachmentsProps<T> {
    context: Context<IDeviceFormContextValue<T>>
}

interface IAttachmentsPropsWithUploads<T> {
    dataset: Datasets
    isLct: boolean
    isAmendment?: boolean
    currentIndex: number
    setCurrentIndex: (step: number) => void
    formData: T
    setFormData: React.Dispatch<React.SetStateAction<T>>
    deviceRef: string | undefined
    saveDraft: (formData: T) => Promise<void>
    saveDraftLoading: boolean
    attachmentUUID: string | undefined
    uploadedAttachments: TAttachmentSummary[] | undefined
    listAttachmentsError: AppError | undefined
    isLoadingUploadedAttachments: boolean
    uploadedAttachmentsError: AppError | undefined
    updateUploadedAttachments: KeyedMutator<{
        attachment_summaries: TAttachmentSummary[]
    }>
    uploadAttachments: (
        attachments: DeviceAttachment[]
    ) => Promise<UploadAttachmentResult[]>
    uploadError: AppError | undefined
    attachmentUploadInProgress: boolean
}

/**
 * A Page showing the currently "staged" attachments a user can upload for a device.
 * Allows the user to add/remove attachments
 */
const Attachments = <T extends DeviceFormDataWithAttachments>({
    dataset,
    isLct,
    isAmendment,
    currentIndex,
    setCurrentIndex,
    formData,
    setFormData,
    deviceRef,
    saveDraft,
    saveDraftLoading,
    attachmentUUID,
    uploadedAttachments,
    listAttachmentsError,
    isLoadingUploadedAttachments,
    uploadedAttachmentsError,
    updateUploadedAttachments,
    uploadAttachments,
    uploadError,
    attachmentUploadInProgress,
}: IAttachmentsPropsWithUploads<T>): ReactElement => {
    const { isTabletOrMob } = useScreenSize()
    const [uploadedAttachmentsExpanded, setUploadedAttachmentsExpanded] =
        useState<boolean>(false)

    const [stagedAttachmentsExpanded, setStagedAttachmentsExpanded] =
        useState<boolean>(true)

    const [failedUploads, setFailedUploads] = useState<
        UploadAttachmentResult[]
    >([])

    const { handleSubmit, control, watch, setValue, register } = useForm({
        defaultValues: mapRegisterNameToValues(formData, uploadedAttachments),
        values: mapRegisterNameToValues(formData, uploadedAttachments),
    })

    const { deleteUploadedAttachment, isLoading: deleteAttachmentInProgress } =
        useDeleteUploadedAttachment(isLct, dataset, isAmendment)

    const {
        setUploadedAttachmentRestricted,
        isLoading: setAttachmentRestrictedInProgress,
    } = useSetUploadedAttachmentRestricted(isLct, dataset, isAmendment)

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !uploadedAttachmentsError &&
        !!uploadedAttachments

    const attachmentEditInProgress =
        deleteAttachmentInProgress || setAttachmentRestrictedInProgress

    /** Deletes an uploaded attachment and updates the list of uploaded attachments */
    const onRemoveUploadedAttachment = async (
        fileName?: string
    ): Promise<void> => {
        await deleteUploadedAttachment(deviceRef, fileName, attachmentUUID)
        updateUploadedAttachments()
    }

    /** Updates the restricted state of an uploaded attachment and updates the list of uploaded attachments */
    const onToggleUploadedAttachment = async (
        fileName: string
    ): Promise<void> => {
        const restricted = watch(
            getAttachmentRestrictedRegisterName(fileName)
        ) as boolean
        await setUploadedAttachmentRestricted(
            restricted,
            fileName,
            deviceRef,
            attachmentUUID
        )
        updateUploadedAttachments()
    }

    /** Returns the Register device form data with updated react-hook-form fields  */
    const getUpdatedFormdata = (): T => {
        const updatedAttachments: DeviceAttachment[] =
            formData.stagedAttachments.map((stagedAttachment) => ({
                file: stagedAttachment.file,
                base64Image: stagedAttachment.base64Image,
                description: watch(
                    getAttachmentDescriptionRegisterName(
                        stagedAttachment.file.name
                    )
                ) as string,
                restricted: watch(
                    getAttachmentRestrictedRegisterName(
                        stagedAttachment.file.name
                    )
                ) as boolean,
            }))

        const updatedFormData: T = {
            ...formData,
            stagedAttachments: updatedAttachments,
        }

        return updatedFormData
    }

    /** Queues a snackbar pop-up to report an error with adding a file to the staging area */
    const reportFailedFileProcessing = (
        file: File,
        message = "Could not process file"
    ): void => {
        snackbar(`${file.name} - ${message}`, "error")
    }

    const reportUploadedAttachmentsNotLoaded = (): void => {
        snackbar("Please wait until uploaded attachments are loaded", "warning")
    }

    /** Adds a successfully read file to the staging area */
    const onFileLoadSuccess = (
        result: string | ArrayBuffer,
        file: File
    ): void => {
        const restrictedRegisterName = getAttachmentRestrictedRegisterName(
            file.name
        )
        register(restrictedRegisterName)
        setValue(restrictedRegisterName, true)

        const stagedAttachment: DeviceAttachment = {
            file,
            base64Image: result.toString(),
            description: "",
            restricted: true,
        }

        const updatedFormData = getUpdatedFormdata()
        setFormData({
            ...updatedFormData,
            stagedAttachments: [
                ...updatedFormData.stagedAttachments,
                stagedAttachment,
            ],
        })
    }

    /** Reports a file after failing processing */
    const onFileLoadError = (file: File, error?: Error | null): void => {
        reportFailedFileProcessing(file, error?.message)
    }

    /** Triggered when a file is successfully validated by the dropzone */
    const onFileDrop = (file: File): void => {
        if (!uploadedAttachments) {
            reportUploadedAttachmentsNotLoaded()
            return
        }

        const reader = new FileReader()

        const fileAlreadyStaged = formData.stagedAttachments.some(
            (attachment) => attachment.file.name === file.name
        )

        const fileAlreadyUploaded = uploadedAttachments.some(
            (summary) => summary.fileName === file.name
        )

        if (fileAlreadyStaged) {
            reportFailedFileProcessing(file, "File already staged")
            return
        }

        if (fileAlreadyUploaded) {
            reportFailedFileProcessing(file, "File already uploaded")
            return
        }

        const onLoad = (result: string | ArrayBuffer): void =>
            onFileLoadSuccess(result, file)
        const onError = (error?: Error | null): void =>
            onFileLoadError(file, error)

        reader.onload = () => {
            if (reader.result) {
                onLoad(reader.result)
            } else {
                onError()
            }
        }

        reader.onerror = () => {
            onError(reader.error)
        }

        reader.readAsDataURL(file)
    }

    /** Is triggered whenever any files are added to the drop */
    const onDrop = (
        acceptedFiles: File[],
        rejectedFiles: FileRejection[]
    ): void => {
        if (!uploadedAttachments) {
            reportUploadedAttachmentsNotLoaded()
            return
        }

        if (
            uploadedAttachments.length +
                acceptedFiles.length +
                formData.stagedAttachments.length >
            DEVICE_MAX_ALLOWED_ATTACHMENTS
        ) {
            snackbar(
                `Exceeded max files: ${DEVICE_MAX_ALLOWED_ATTACHMENTS}`,
                "error"
            )
            return
        }
        acceptedFiles.forEach((file) => onFileDrop(file))
        rejectedFiles.forEach((rejectedFile) => {
            const error = rejectedFile.errors[0]

            if (error.code === ErrorCode.FileTooLarge) {
                reportFailedFileProcessing(
                    rejectedFile.file,
                    `File is larger than ${bytesToMb(
                        DEVICE_MAX_FILE_SIZE_BYTES
                    )} MB`
                )
            } else {
                reportFailedFileProcessing(
                    rejectedFile.file,
                    rejectedFile.errors[0]?.message
                )
            }
        })
    }

    /** Remove the associated files for the provided result from the staging area */
    const removeSuccessfulUploadsFromStagingArea = (
        data: T,
        successfulFiles: UploadAttachmentResult[]
    ): T => {
        const successfulFileNames = successfulFiles.map(
            (successfulFile) => successfulFile.fileName
        )

        const updatedAttachments = data.stagedAttachments.filter(
            (attachment) => !successfulFileNames.includes(attachment.file.name)
        )

        const newData: T = {
            ...data,
            stagedAttachments: updatedAttachments,
        }

        return newData
    }

    /**
     * Attempts to upload the attachments in the staging area
     * Successful uploads are removed from the staging area, failed uploads are retained
     */
    const uploadStagedAttachments = async (): Promise<
        [UploadAttachmentResult[], UploadAttachmentResult[]]
    > => {
        const updatedFormData = getUpdatedFormdata()

        const uploadResults = await uploadAttachments(
            updatedFormData.stagedAttachments
        )

        const failedFiles = uploadResults.filter(
            (uploadResult) => uploadResult.result === "rejected"
        )

        const successfulFiles = uploadResults.filter(
            (uploadResult) => uploadResult.result === "fulfilled"
        )

        const updatedStagingAreaFormData =
            removeSuccessfulUploadsFromStagingArea(
                updatedFormData,
                successfulFiles
            )

        setFailedUploads(failedFiles)

        setFormData(updatedStagingAreaFormData)

        updateUploadedAttachments()

        return [successfulFiles, failedFiles]
    }

    /** Removes a single file from the staging area */
    const removeStagedAttachment = (filename: string): void => {
        const updatedAttachments = formData.stagedAttachments.filter(
            (attachment) => attachment.file.name !== filename
        )

        const updateFormData: T = {
            ...formData,
            stagedAttachments: updatedAttachments,
        }

        setFormData(updateFormData)
    }

    const onClickBack = (): void => {
        const updatedFormData = getUpdatedFormdata()
        setFormData(updatedFormData)
        setCurrentIndex(currentIndex - 1)
    }

    /**
     * Uploads staged attachments.
     * If there are no failed uploads, progresses to the next page.
     */
    const onClickNext = async (): Promise<void> => {
        if (formData.stagedAttachments.length === 0) {
            setCurrentIndex(currentIndex + 1)
            return
        }

        const [, failedFiles] = await uploadStagedAttachments()

        if (failedFiles.length === 0) {
            setCurrentIndex(currentIndex + 1)
        }
    }

    const onSaveDraft = async (): Promise<void> => {
        await uploadStagedAttachments()
        const updatedFormData = getUpdatedFormdata()
        saveDraft(updatedFormData)
    }

    if (uploadError) {
        return <ErrorDisplay error={uploadError} />
    }

    if (listAttachmentsError) {
        return <ErrorDisplay error={listAttachmentsError} />
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Upload Supporting Files"
                    subHeader="Please use this section to upload relevant files to support your device assessment. These files can be technical specifications, test reports, or any other relevant documentation"
                    index={currentIndex + 1}
                />
                <Collapse
                    in={failedUploads.length > 0 && !attachmentUploadInProgress}
                >
                    <AppAlert severity="error">
                        <AlertTitle>
                            Failed to upload the following files
                        </AlertTitle>
                        <ul>
                            {failedUploads.map((failedUpload) => (
                                <li key={failedUpload.fileName}>
                                    {failedUpload.fileName} -{" "}
                                    {failedUpload.reason}
                                </li>
                            ))}
                        </ul>
                    </AppAlert>
                </Collapse>
                <Stack
                    direction={isTabletOrMob ? "column-reverse" : "row"}
                    spacing={GRID_SPACING * 2}
                    width="100%"
                >
                    <Box flexGrow={1}>
                        <AppDropzone
                            minFileSize={DEVICE_MIN_FILE_SIZE_BYTES}
                            maxFileSize={DEVICE_MAX_FILE_SIZE_BYTES}
                            maxFilesPerDrop={DEVICE_MAX_FILES_PER_DROP}
                            onDrop={onDrop}
                            disabled={attachmentUploadInProgress}
                        />
                    </Box>
                    <Box>
                        <AppAlert
                            severity="info"
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 2,
                            }}
                        >
                            <AlertTitle sx={{ width: "100%" }}>
                                Please use this dropzone to upload your file(s)
                            </AlertTitle>

                            <Typography>File Upload Top Tips:</Typography>
                            <CheckMarkList
                                items={[
                                    "You can upload up to 10 files per device",
                                    "Each file must be less than 25 MB in size",
                                    "You must indicate if the file is restricted or not",
                                    "Restricted files will only be visible to ENA, ENA evaluators and DNOs",
                                    "No restricted files will be visible to any public users",
                                ]}
                            />
                        </AppAlert>
                    </Box>
                </Stack>
                {formData.stagedAttachments.length > 0 && (
                    <Box display="flex" alignItems="center">
                        <AppCardHeader
                            title={`Ready to upload (${formData.stagedAttachments.length})`}
                            sx={{
                                marginBottom: 0,
                                marginRight: 3,
                                color: "text.secondary",
                            }}
                        />
                        <Box flex="1">
                            <Divider />
                        </Box>
                        <IconButton
                            onClick={() =>
                                setStagedAttachmentsExpanded((prev) => !prev)
                            }
                            sx={{
                                marginLeft: 3,
                            }}
                        >
                            {stagedAttachmentsExpanded ? (
                                <ExpandLess fontSize="large" color="primary" />
                            ) : (
                                <ExpandMore fontSize="large" color="primary" />
                            )}
                        </IconButton>
                    </Box>
                )}

                <DisplayIfElse
                    condition={isLoadingUploadedAttachments}
                    displayIfTrue={<DeviceAttachmentAccordionSkeleton />}
                    displayIfFalse={
                        <>
                            <Collapse in={stagedAttachmentsExpanded}>
                                <TransitionGroup
                                    component={Stack}
                                    spacing={GRID_SPACING}
                                >
                                    {formData.stagedAttachments.map(
                                        (attachment) => (
                                            <Collapse
                                                key={attachment.file.name}
                                            >
                                                <AttachmentMetaForm
                                                    base64Image={
                                                        attachment.base64Image
                                                    }
                                                    control={control}
                                                    fileName={
                                                        attachment.file.name
                                                    }
                                                    onRemove={() =>
                                                        removeStagedAttachment(
                                                            attachment.file.name
                                                        )
                                                    }
                                                    state="staged"
                                                    disabled={
                                                        attachmentEditInProgress ||
                                                        attachmentUploadInProgress
                                                    }
                                                />
                                            </Collapse>
                                        )
                                    )}
                                </TransitionGroup>
                            </Collapse>
                            {uploadedAttachmentsAreValid && (
                                <>
                                    <Box display="flex" alignItems="center">
                                        <AppCardHeader
                                            title={`Uploaded Files (${uploadedAttachments.length})`}
                                            sx={{
                                                marginBottom: 0,
                                                marginRight: 3,
                                                color: "text.secondary",
                                            }}
                                        />
                                        <Box flex="1">
                                            <Divider />
                                        </Box>
                                        <IconButton
                                            onClick={() =>
                                                setUploadedAttachmentsExpanded(
                                                    (prev) => !prev
                                                )
                                            }
                                            sx={{
                                                marginLeft: 3,
                                            }}
                                        >
                                            {uploadedAttachmentsExpanded ? (
                                                <ExpandLess
                                                    fontSize="large"
                                                    color="primary"
                                                />
                                            ) : (
                                                <ExpandMore
                                                    fontSize="large"
                                                    color="primary"
                                                />
                                            )}
                                        </IconButton>
                                    </Box>
                                    {attachmentEditInProgress && (
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    marginRight: 1,
                                                }}
                                            >
                                                Updating attachments
                                            </Typography>
                                            <CircularProgress size={20} />
                                        </Box>
                                    )}
                                    <Collapse in={uploadedAttachmentsExpanded}>
                                        <TransitionGroup
                                            component={Stack}
                                            spacing={GRID_SPACING}
                                        >
                                            {uploadedAttachments.map(
                                                (uploadedAttachment) => (
                                                    <Collapse
                                                        key={
                                                            uploadedAttachment.fileName
                                                        }
                                                        in
                                                    >
                                                        <AttachmentMetaForm
                                                            base64Image=""
                                                            control={control}
                                                            fileName={
                                                                uploadedAttachment.fileName
                                                            }
                                                            onRemove={() =>
                                                                onRemoveUploadedAttachment(
                                                                    uploadedAttachment.fileName
                                                                )
                                                            }
                                                            onToggle={() =>
                                                                onToggleUploadedAttachment(
                                                                    uploadedAttachment.fileName
                                                                )
                                                            }
                                                            canEditDescription={
                                                                false
                                                            }
                                                            description={
                                                                uploadedAttachment.description
                                                            }
                                                            state="uploaded"
                                                            disabled={
                                                                attachmentEditInProgress ||
                                                                attachmentUploadInProgress
                                                            }
                                                        />
                                                    </Collapse>
                                                )
                                            )}
                                        </TransitionGroup>
                                    </Collapse>
                                </>
                            )}
                        </>
                    }
                />
                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={() => handleSubmit(onClickNext)()}
                    nextText={
                        formData.stagedAttachments.length > 0
                            ? "Upload"
                            : "Next"
                    }
                    backText="Back"
                    nextIcon={
                        attachmentUploadInProgress ? (
                            <CircularProgress size={20} />
                        ) : undefined
                    }
                    allDisabled={
                        attachmentUploadInProgress || attachmentEditInProgress
                    }
                    onClickMiddle={() => handleSubmit(onSaveDraft)()}
                    middleDisabled={saveDraftLoading}
                />
            </Stack>
        </AppCard>
    )
}

Attachments.defaultProps = {
    isAmendment: false,
}

export default Attachments
